import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Shield,
  Lock,
  KeyRound,
  FileCheck,
  Scale,
  AlertTriangle,
  ShieldCheck,
  ShieldAlert,
  Eye,
  EyeOff,
  Bug,
  Fingerprint,
  BarChart3,
  Users,
  Search,
  FileText,
  GraduationCap,
  ArrowRight,
  Check,
  ChevronRight,
  Landmark,
  CreditCard,
  Stethoscope,
  Globe,
  Building2,
  ShoppingCart,
  Car,
  Gavel,
  CircuitBoard,
  AlertOctagon,
  Activity,
  X,
  Send,
  Loader,
  Calendar
} from 'lucide-react';

// API Base URL
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// ============= SECURITY ASSESSMENT MODAL =============
const SecurityAssessmentModal = ({ isOpen, onClose, selectedPlan = null }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    industry: '',
    aiSystems: '',
    concerns: '',
    compliance: '',
    package: selectedPlan || '',
    message: ''
  });
  const [status, setStatus] = useState({ type: '', message: '' });

  // Update package when selectedPlan changes
  React.useEffect(() => {
    if (selectedPlan) {
      setFormData(prev => ({ ...prev, package: selectedPlan }));
    }
  }, [selectedPlan]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.company || !formData.concerns) {
      setStatus({ type: 'error', message: 'Please fill in all required fields' });
      return;
    }

    setStatus({ type: 'loading', message: '' });

    try {
      const response = await fetch(`${API_BASE_URL}/api/send-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone || 'Not provided',
          interest: 'AI Security & Governance',
          message: `Security Assessment Request\n\nCompany: ${formData.company}\nIndustry: ${formData.industry || 'Not specified'}\nAI Systems in Use: ${formData.aiSystems || 'Not specified'}\nCompliance Requirements: ${formData.compliance || 'Not specified'}\nPackage Interest: ${formData.package || 'Not specified'}\n\nSecurity Concerns:\n${formData.concerns}\n\nAdditional Details:\n${formData.message || 'None provided'}`
        }),
      });

      const data = await response.json();

      if (data.success) {
        setStatus({ type: 'success', message: 'Request submitted successfully!' });
        setFormData({ name: '', email: '', phone: '', company: '', industry: '', aiSystems: '', concerns: '', compliance: '', package: '', message: '' });
      } else {
        throw new Error(data.error || 'Failed to submit');
      }
    } catch (error) {
      console.error('Assessment error:', error);
      setStatus({ type: 'error', message: 'Failed to submit. Please try again.' });
    }
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (status.type === 'error') setStatus({ type: '', message: '' });
  };

  const resetModal = () => {
    setStatus({ type: '', message: '' });
    setFormData({ name: '', email: '', phone: '', company: '', industry: '', aiSystems: '', concerns: '', compliance: '', package: selectedPlan || '', message: '' });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="relative bg-slate-950 backdrop-blur-xl border border-white/[0.08] rounded-3xl p-8 max-w-md w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-emerald-500/5 rounded-3xl" />
        
        <button
          onClick={resetModal}
          className="absolute top-4 right-4 text-gray-400 hover:text-white transition z-10"
        >
          <X size={24} />
        </button>

        {status.type === 'success' ? (
          <motion.div 
            className="text-center py-8 relative z-10"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <div className="mb-4 inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-blue-500/20 to-emerald-500/20 border border-teal-500/30">
              <ShieldCheck size={32} className="text-teal-300" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-2">Assessment Scheduled!</h3>
            <p className="text-gray-400 mb-6">
              Our security team will review your requirements and contact you within 24 hours to schedule your assessment.
            </p>
            <button
              onClick={resetModal}
              className="px-6 py-2 bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 rounded-full text-white font-semibold hover:shadow-lg hover:shadow-teal-500/30 transition"
            >
              Close
            </button>
          </motion.div>
        ) : (
          <form onSubmit={handleSubmit} className="relative z-10">
            <div className="mb-2 inline-flex items-center gap-2 px-3 py-1 bg-gradient-to-r from-blue-500/20 to-emerald-500/20 border border-teal-500/30 rounded-full">
              <Shield size={14} className="text-teal-400" />
              <span className="text-xs text-teal-400 font-semibold">Security Assessment</span>
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Request Assessment</h2>
            <p className="text-teal-400 mb-6">Protect your AI systems proactively</p>

            {status.type === 'error' && (
              <div className="mb-4 p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                {status.message}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Full Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-teal-500 focus:outline-none transition"
                  placeholder="Enter your full name"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Work Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-teal-500 focus:outline-none transition"
                  placeholder="Enter your work email"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Phone Number</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-teal-500 focus:outline-none transition"
                  placeholder="Enter your phone number"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Company Name *</label>
                <input
                  type="text"
                  value={formData.company}
                  onChange={(e) => handleChange('company', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-teal-500 focus:outline-none transition"
                  placeholder="Enter your company name"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">Industry</label>
                  <select
                    value={formData.industry}
                    onChange={(e) => handleChange('industry', e.target.value)}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-teal-500 focus:outline-none transition"
                  >
                    <option value="" className="bg-slate-950">Select</option>
                    <option value="Finance / Banking" className="bg-slate-950">Finance</option>
                    <option value="Healthcare" className="bg-slate-950">Healthcare</option>
                    <option value="E-commerce" className="bg-slate-950">E-commerce</option>
                    <option value="Automotive" className="bg-slate-950">Automotive</option>
                    <option value="Legal" className="bg-slate-950">Legal</option>
                    <option value="Enterprise SaaS" className="bg-slate-950">SaaS</option>
                    <option value="Government" className="bg-slate-950">Government</option>
                    <option value="Other" className="bg-slate-950">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">AI Systems</label>
                  <select
                    value={formData.aiSystems}
                    onChange={(e) => handleChange('aiSystems', e.target.value)}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-teal-500 focus:outline-none transition"
                  >
                    <option value="" className="bg-slate-950">Select</option>
                    <option value="ML Models" className="bg-slate-950">ML Models</option>
                    <option value="LLMs / Chatbots" className="bg-slate-950">LLMs / Chatbots</option>
                    <option value="Computer Vision" className="bg-slate-950">Computer Vision</option>
                    <option value="Recommendation Systems" className="bg-slate-950">Recommendation</option>
                    <option value="Fraud Detection" className="bg-slate-950">Fraud Detection</option>
                    <option value="Multiple Systems" className="bg-slate-950">Multiple</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Compliance Requirements</label>
                <select
                  value={formData.compliance}
                  onChange={(e) => handleChange('compliance', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-teal-500 focus:outline-none transition"
                >
                  <option value="" className="bg-slate-950">Select requirements</option>
                  <option value="GDPR" className="bg-slate-950">GDPR</option>
                  <option value="HIPAA" className="bg-slate-950">HIPAA</option>
                  <option value="SOC 2" className="bg-slate-950">SOC 2</option>
                  <option value="PCI DSS" className="bg-slate-950">PCI DSS</option>
                  <option value="EU AI Act" className="bg-slate-950">EU AI Act</option>
                  <option value="ISO 42001" className="bg-slate-950">ISO 42001</option>
                  <option value="Multiple" className="bg-slate-950">Multiple Standards</option>
                  <option value="Not Sure" className="bg-slate-950">Not Sure</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Package Interest</label>
                <select
                  value={formData.package}
                  onChange={(e) => handleChange('package', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-teal-500 focus:outline-none transition"
                >
                  <option value="" className="bg-slate-950">Select package</option>
                  <option value="Security Audit ($25K)" className="bg-slate-950">Security Audit ($25K)</option>
                  <option value="Secure Implementation ($75K)" className="bg-slate-950">Secure Implementation ($75K)</option>
                  <option value="Enterprise Protection ($15K/mo)" className="bg-slate-950">Enterprise Protection ($15K/mo)</option>
                  <option value="Custom / Not Sure" className="bg-slate-950">Custom / Not Sure</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Primary Security Concerns *</label>
                <textarea
                  value={formData.concerns}
                  onChange={(e) => handleChange('concerns', e.target.value)}
                  rows="3"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-teal-500 focus:outline-none transition resize-none"
                  placeholder="Describe your main security concerns or vulnerabilities you want to address..."
                />
              </div>

              <button
                type="submit"
                disabled={status.type === 'loading'}
                className="w-full py-3 bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 rounded-full text-white font-semibold hover:shadow-lg hover:shadow-teal-500/30 transition disabled:opacity-70 flex items-center justify-center gap-2"
              >
                {status.type === 'loading' ? (
                  <>
                    <Loader size={18} className="animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Shield size={18} />
                    Request Assessment
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </motion.div>
    </div>
  );
};

// ============= NAVBAR =============
const Navbar = ({ onAssessment }) => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <motion.nav
      className={`fixed w-full top-0 z-50 transition-all duration-300 ${
        scrolled ? 'backdrop-blur-xl bg-slate-950/80 border-b border-white/5' : 'backdrop-blur-sm bg-transparent'
      }`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-gradient-to-r from-blue-500 to-teal-500" />
            <span className="text-white">AI Factory</span>
          </Link>

          <div className="hidden md:flex items-center gap-12">
            <Link to="/" className="text-sm text-gray-300 hover:text-white transition">Home</Link>
            <Link to="/services" className="text-sm text-gray-300 hover:text-white transition">Services</Link>
            <a href="#threats" className="text-sm text-gray-300 hover:text-white transition">Threats</a>
            <a href="#pricing" className="text-sm text-gray-300 hover:text-white transition">Pricing</a>
          </div>

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onAssessment}
            className="hidden md:block px-6 py-2.5 bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 rounded-full text-sm font-semibold text-white hover:shadow-lg hover:shadow-teal-500/50 transition"
          >
            Security Assessment
          </motion.button>
        </div>
      </div>
    </motion.nav>
  );
};

// ============= GRADIENT ORBS BACKGROUND =============
const GradientOrbs = () => {
  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden">
      <motion.div
        className="absolute -top-40 -left-40 w-96 h-96 bg-blue-600/20 rounded-full blur-3xl"
        animate={{
          x: [0, 30, -30, 0],
          y: [0, 40, -40, 0],
        }}
        transition={{ duration: 8, repeat: Infinity }}
      />
      <motion.div
        className="absolute top-1/2 -right-40 w-96 h-96 bg-emerald-600/20 rounded-full blur-3xl"
        animate={{
          x: [0, -30, 30, 0],
          y: [0, -40, 40, 0],
        }}
        transition={{ duration: 10, repeat: Infinity }}
      />
      <motion.div
        className="absolute -bottom-40 left-1/2 w-96 h-96 bg-teal-600/15 rounded-full blur-3xl"
        animate={{
          x: [0, 40, -40, 0],
          y: [0, 30, -30, 0],
        }}
        transition={{ duration: 12, repeat: Infinity }}
      />
    </div>
  );
};

// ============= HERO SECTION =============
const Hero = ({ onAssessment }) => {
  const stats = [
    { value: 'Zero', label: 'Security Breaches' },
    { value: '100%', label: 'Compliance Rate' },
    { value: '24/7', label: 'Threat Monitoring' },
    { value: '50+', label: 'Frameworks Certified' },
  ];

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
      <GradientOrbs />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_80%_at_50%_-20%,rgba(20,184,166,0.2),rgba(255,255,255,0))]" />

      <motion.div
        className="relative z-10 text-center max-w-5xl mx-auto px-4 py-20"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        {/* Badges */}
        <motion.div
          className="flex flex-wrap justify-center gap-3 mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          {['Secure by Design', 'Compliance Ready', 'Risk Managed'].map((badge, i) => (
            <span
              key={i}
              className="text-xs font-semibold text-slate-900 px-4 py-2 rounded-full bg-gradient-to-r from-teal-400 to-emerald-400"
            >
              {badge}
            </span>
          ))}
        </motion.div>

        {/* Title */}
        <motion.h1
          className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight tracking-tight"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          <span className="text-white">AI Security &</span>
          <br />
          <span className="bg-gradient-to-r from-blue-400 via-teal-400 to-emerald-400 bg-clip-text text-transparent">
            Governance
          </span>
        </motion.h1>

        {/* Subtitle */}
        <motion.p
          className="text-xl md:text-2xl text-gray-300 mb-4 font-light tracking-wide"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          Protecting Your AI Systems From Threats & Ensuring Responsible Use
        </motion.p>

        {/* Description */}
        <motion.p
          className="text-base md:text-lg text-gray-400 mb-10 max-w-3xl mx-auto leading-relaxed"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
        >
          AI brings powerful capabilities—but also new risks. We secure your AI systems against 
          attacks, ensure regulatory compliance, protect sensitive data, and implement ethical 
          frameworks. Deploy AI with confidence knowing your systems are protected, compliant, 
          and responsible.
        </motion.p>

        {/* CTA Buttons */}
        <motion.div
          className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onAssessment}
            className="px-8 py-4 bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 rounded-full text-white font-semibold hover:shadow-2xl hover:shadow-teal-500/50 transition flex items-center gap-2"
          >
            Request Assessment <ArrowRight size={18} />
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => document.getElementById('case-studies').scrollIntoView({ behavior: 'smooth' })}
            className="px-8 py-4 border border-white/20 rounded-full text-white font-semibold hover:bg-white/10 transition backdrop-blur-sm hover:border-teal-400/40"
          >
            View Case Studies
          </motion.button>
        </motion.div>

        {/* Stats */}
        <motion.div
          className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7 }}
        >
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              className="relative group"
              whileHover={{ y: -5 }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-teal-500/20 to-blue-500/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="relative bg-white/[0.03] backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:border-teal-500/30 transition-all">
                <div className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-teal-400 to-emerald-400 bg-clip-text text-transparent mb-2">
                  {stat.value}
                </div>
                <div className="text-sm text-gray-400 uppercase tracking-wider">
                  {stat.label}
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
};

// ============= SERVICE CARD =============
const ServiceCard = ({ service, index }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <motion.div
      className="relative group h-full"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      viewport={{ once: true, margin: "-100px" }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 via-transparent to-emerald-500/20 rounded-3xl blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

      <div className="relative bg-gradient-to-br from-white/[0.07] to-white/[0.02] backdrop-blur-xl border border-white/10 rounded-3xl p-8 hover:border-teal-400/30 transition-all duration-500 h-full flex flex-col overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 rounded-t-3xl" />

        <div className="relative z-10 flex flex-col h-full">
          <motion.div
            className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500/30 to-emerald-500/30 flex items-center justify-center mb-6 group-hover:from-blue-500/50 group-hover:to-emerald-500/50 transition-all"
            animate={isHovered ? { rotate: [0, -10, 10, 0] } : {}}
            transition={{ duration: 0.5 }}
          >
            <service.icon className="text-teal-300" size={32} />
          </motion.div>

          <h3 className="text-xl font-bold text-white mb-4 group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-blue-400 group-hover:via-teal-400 group-hover:to-emerald-400 group-hover:bg-clip-text transition-all">
            {service.title}
          </h3>

          <p className="text-gray-400 mb-6 leading-relaxed flex-grow">
            {service.description}
          </p>

          <ul className="space-y-3">
            {service.features.map((feature, i) => (
              <motion.li
                key={i}
                className="text-sm text-gray-400 flex items-start gap-3"
                initial={{ opacity: 0, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 * i }}
              >
                <ChevronRight size={14} className="text-emerald-400 mt-1 flex-shrink-0" />
                <span>{feature}</span>
              </motion.li>
            ))}
          </ul>
        </div>
      </div>
    </motion.div>
  );
};

// ============= SERVICES SECTION =============
const ServicesSection = () => {
  const services = [
    {
      icon: ShieldCheck,
      title: 'AI Security Assessment',
      description: 'Identify vulnerabilities in your AI systems before attackers do. We conduct comprehensive security audits covering model robustness, data protection, infrastructure security, and access controls.',
      features: [
        'Adversarial attack vulnerability testing',
        'Data pipeline security analysis',
        'Model extraction risk assessment',
        'Infrastructure penetration testing',
        'API security evaluation'
      ]
    },
    {
      icon: Lock,
      title: 'Model Protection',
      description: 'Safeguard your proprietary AI models from theft, reverse engineering, and adversarial manipulation. Implement watermarking, encryption, and secure serving infrastructure.',
      features: [
        'Model watermarking & fingerprinting',
        'Encrypted model serving',
        'Anti-tampering mechanisms',
        'Secure model versioning',
        'IP protection strategies'
      ]
    },
    {
      icon: KeyRound,
      title: 'Data Privacy & Protection',
      description: 'Ensure sensitive data remains private throughout the AI lifecycle. Implement differential privacy, data anonymization, federated learning, and secure multi-party computation.',
      features: [
        'Differential privacy implementation',
        'PII detection & anonymization',
        'Federated learning architecture',
        'Data lineage & tracking',
        'Secure data sharing protocols'
      ]
    },
    {
      icon: FileCheck,
      title: 'Compliance & Regulatory',
      description: 'Navigate complex AI regulations including GDPR, EU AI Act, CCPA, and industry-specific requirements. Achieve and maintain compliance with automated monitoring and reporting.',
      features: [
        'GDPR & CCPA compliance audits',
        'EU AI Act readiness assessment',
        'Industry-specific certifications',
        'Compliance documentation generation',
        'Regulatory change monitoring'
      ]
    },
    {
      icon: Scale,
      title: 'Ethical AI Framework',
      description: 'Build responsible AI systems that are fair, transparent, and accountable. Detect and mitigate bias, ensure explainability, and establish ethical governance processes.',
      features: [
        'Bias detection & mitigation',
        'Explainability & interpretability',
        'Fairness metrics & monitoring',
        'Ethics review board setup',
        'Responsible AI policies'
      ]
    },
    {
      icon: AlertTriangle,
      title: 'Threat Detection & Response',
      description: '24/7 monitoring for AI-specific threats including adversarial attacks, model poisoning, and data drift. Rapid incident response with automated remediation and forensics.',
      features: [
        'Real-time threat monitoring',
        'Anomaly detection systems',
        'Adversarial attack detection',
        'Incident response playbooks',
        'Security operations center (SOC)'
      ]
    }
  ];

  return (
    <section id="services" className="relative py-24 px-4 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <span className="text-xs font-semibold text-teal-300 px-4 py-2 rounded-full border border-teal-500/50 bg-teal-500/10 inline-block mb-4">
            Protection Services
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            AI Security Solutions
          </h2>
          <p className="text-gray-400 max-w-3xl mx-auto text-lg">
            Comprehensive security services to protect your AI systems from adversarial attacks, 
            data breaches, model theft, and emerging threats in the AI landscape.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <ServiceCard key={index} service={service} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

// ============= THREAT LANDSCAPE SECTION =============
const ThreatLandscapeSection = () => {
  const threats = [
    {
      level: 'CRITICAL',
      title: 'Adversarial Attacks',
      description: 'Malicious inputs designed to fool AI models into making incorrect predictions. Can bypass security systems, manipulate decisions, or cause system failures.',
      mitigation: 'Adversarial training, input validation, ensemble models, and real-time attack detection systems.'
    },
    {
      level: 'HIGH',
      title: 'Model Extraction',
      description: 'Attackers query your model repeatedly to reconstruct it, stealing your intellectual property and competitive advantage without access to training data or code.',
      mitigation: 'Query rate limiting, watermarking, model obfuscation, and behavioral monitoring for suspicious access patterns.'
    },
    {
      level: 'CRITICAL',
      title: 'Data Poisoning',
      description: 'Injecting malicious data into training sets to corrupt model behavior. Can cause models to make biased decisions, leak information, or fail on specific inputs.',
      mitigation: 'Data validation pipelines, anomaly detection, robust training algorithms, and secure data provenance tracking.'
    },
    {
      level: 'HIGH',
      title: 'Privacy Leakage',
      description: 'Models can inadvertently memorize and reveal sensitive training data through membership inference, model inversion, or attribute inference attacks.',
      mitigation: 'Differential privacy, secure aggregation, federated learning, and privacy audits to prevent data leakage.'
    },
    {
      level: 'MEDIUM',
      title: 'Model Backdoors',
      description: 'Hidden triggers embedded during training that cause models to behave maliciously when specific patterns appear, while functioning normally otherwise.',
      mitigation: 'Model auditing, neural network inspection, trigger detection, and secure supply chain for pre-trained models.'
    },
    {
      level: 'HIGH',
      title: 'Prompt Injection',
      description: 'For LLMs and chatbots, attackers craft inputs that override system instructions, extract private data, or manipulate model behavior for unauthorized actions.',
      mitigation: 'Input sanitization, prompt guardrails, output filtering, and reinforcement learning from human feedback (RLHF).'
    }
  ];

  const getLevelColor = (level) => {
    switch (level) {
      case 'CRITICAL': return 'from-red-500 to-orange-500';
      case 'HIGH': return 'from-orange-500 to-amber-500';
      case 'MEDIUM': return 'from-amber-500 to-yellow-500';
      default: return 'from-teal-500 to-emerald-500';
    }
  };

  return (
    <section id="threats" className="relative py-24 px-4 overflow-hidden bg-slate-950/50">
      <div className="max-w-7xl mx-auto">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <span className="text-xs font-semibold text-teal-300 px-4 py-2 rounded-full border border-teal-500/50 bg-teal-500/10 inline-block mb-4">
            Threat Intelligence
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            AI Security Threats
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg">
            Understanding the threat landscape is crucial for protection. Here are the most 
            critical risks facing AI systems today and how we mitigate them.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {threats.map((threat, index) => (
            <motion.div
              key={index}
              className="group relative"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <div className="bg-gradient-to-br from-white/[0.07] to-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-6 hover:border-teal-500/30 transition-all duration-300 h-full">
                <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold text-white bg-gradient-to-r ${getLevelColor(threat.level)} mb-4`}>
                  {threat.level}
                </span>

                <div className="absolute top-6 right-6 opacity-10">
                  <AlertOctagon size={48} className="text-teal-400" />
                </div>

                <h3 className="text-xl font-bold text-teal-400 mb-3">
                  {threat.title}
                </h3>

                <p className="text-gray-400 mb-6 leading-relaxed text-sm">
                  {threat.description}
                </p>

                <div className="bg-gradient-to-br from-teal-500/10 to-blue-500/10 border-l-4 border-teal-500 rounded-r-xl p-4">
                  <p className="text-xs font-bold text-teal-400 mb-2 uppercase tracking-wider">Our Defense:</p>
                  <p className="text-sm text-gray-300">
                    {threat.mitigation}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============= GOVERNANCE FRAMEWORK SECTION =============
const GovernanceSection = () => {
  const pillars = [
    {
      icon: BarChart3,
      title: 'Risk Management',
      description: 'Identify, assess, and mitigate AI-specific risks across technical, ethical, legal, and reputational dimensions with continuous monitoring.'
    },
    {
      icon: Users,
      title: 'Accountability',
      description: 'Clear ownership and responsibility structures for AI decisions, with audit trails, decision logging, and human oversight mechanisms.'
    },
    {
      icon: Search,
      title: 'Transparency',
      description: 'Explainable AI systems with documentation on data sources, model architecture, training processes, and decision-making logic.'
    },
    {
      icon: Scale,
      title: 'Fairness',
      description: 'Bias detection and mitigation across protected groups, ensuring equitable outcomes and preventing discriminatory decision-making.'
    },
    {
      icon: FileText,
      title: 'Policy Framework',
      description: 'Comprehensive policies covering acceptable use, data handling, model development, deployment standards, and incident response.'
    },
    {
      icon: GraduationCap,
      title: 'Training & Culture',
      description: 'Organization-wide AI literacy programs fostering responsible development and deployment practices across all teams.'
    }
  ];

  return (
    <section className="relative py-24 px-4 overflow-hidden">
      <div className="max-w-6xl mx-auto">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <span className="text-xs font-semibold text-teal-300 px-4 py-2 rounded-full border border-teal-500/50 bg-teal-500/10 inline-block mb-4">
            Governance Framework
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            AI Governance Pillars
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg">
            Establish comprehensive governance that ensures your AI systems are accountable, 
            transparent, fair, and aligned with your organizational values and legal obligations.
          </p>
        </motion.div>

        <motion.div
          className="bg-gradient-to-br from-white/[0.07] to-white/[0.02] backdrop-blur-xl border border-white/10 rounded-3xl p-8 md:p-12"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {pillars.map((pillar, index) => (
              <motion.div
                key={index}
                className="group p-6 bg-white/[0.03] border border-white/10 rounded-2xl hover:border-teal-500/30 transition-all"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
              >
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500/30 to-emerald-500/30 flex items-center justify-center mb-4 group-hover:from-blue-500/50 group-hover:to-emerald-500/50 transition-all">
                  <pillar.icon className="text-teal-300" size={24} />
                </div>
                <h3 className="text-lg font-bold text-teal-400 mb-2">
                  {pillar.title}
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  {pillar.description}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// ============= COMPLIANCE SECTION =============
const ComplianceSection = () => {
  const standards = [
    { icon: Globe, name: 'EU AI ACT', description: "Comprehensive compliance with Europe's landmark AI regulation, including risk classification and mandatory requirements." },
    { icon: Lock, name: 'GDPR', description: 'Data protection compliance for AI systems processing personal data, including right to explanation and consent.' },
    { icon: Shield, name: 'CCPA / CPRA', description: 'California privacy law compliance for AI systems, covering data rights, automated decision-making, and transparency.' },
    { icon: Stethoscope, name: 'HIPAA', description: 'Healthcare AI compliance ensuring protection of patient data and meeting medical privacy requirements.' },
    { icon: CreditCard, name: 'PCI DSS', description: 'Payment card industry standards for AI systems handling financial transactions and payment data.' },
    { icon: Landmark, name: 'SOC 2', description: 'Security, availability, and confidentiality controls for AI systems handling customer data.' },
    { icon: CircuitBoard, name: 'ISO 42001', description: 'AI management system certification demonstrating responsible AI development and deployment practices.' },
    { icon: Activity, name: 'NIST AI RMF', description: 'US National Institute framework for trustworthy AI systems with risk management best practices.' }
  ];

  return (
    <section className="relative py-24 px-4 overflow-hidden bg-slate-950/50">
      <div className="max-w-7xl mx-auto">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <span className="text-xs font-semibold text-teal-300 px-4 py-2 rounded-full border border-teal-500/50 bg-teal-500/10 inline-block mb-4">
            Regulatory Compliance
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Standards & Certifications
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg">
            Navigate the complex regulatory landscape with expertise across global frameworks, 
            industry standards, and emerging AI-specific regulations.
          </p>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {standards.map((standard, index) => (
            <motion.div
              key={index}
              className="group text-center"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.05 }}
              viewport={{ once: true }}
            >
              <motion.div
                className="bg-gradient-to-br from-white/[0.07] to-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-6 hover:border-teal-500/30 transition-all duration-300 h-full"
                whileHover={{ y: -5 }}
              >
                <div className="w-14 h-14 mx-auto rounded-xl bg-gradient-to-br from-blue-500/30 to-emerald-500/30 flex items-center justify-center mb-4 group-hover:from-blue-500/50 group-hover:to-emerald-500/50 transition-all">
                  <standard.icon className="text-teal-300" size={28} />
                </div>
                <h3 className="text-lg font-bold text-transparent bg-gradient-to-r from-blue-400 to-teal-400 bg-clip-text mb-2">
                  {standard.name}
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  {standard.description}
                </p>
              </motion.div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============= PRICING SECTION =============
const PricingSection = ({ onSelectPlan }) => {
  const plans = [
    {
      tier: 'Security Audit',
      price: '$25K',
      period: '4-6 Week Assessment',
      featured: false,
      planValue: 'Security Audit ($25K)',
      features: [
        'Comprehensive security assessment',
        'Vulnerability identification',
        'Threat modeling & analysis',
        'Compliance gap analysis',
        'Risk prioritization matrix',
        'Detailed remediation roadmap',
        'Executive presentation'
      ]
    },
    {
      tier: 'Secure Implementation',
      price: '$75K',
      period: '3-4 Month Project',
      featured: true,
      planValue: 'Secure Implementation ($75K)',
      features: [
        'Complete security architecture',
        'Threat detection systems',
        'Privacy protection implementation',
        'Compliance framework setup',
        'Governance policy development',
        'Team training & documentation',
        '3 months post-implementation support',
        'Continuous monitoring setup'
      ]
    },
    {
      tier: 'Enterprise Protection',
      price: '$15K/mo',
      period: 'Managed Service',
      featured: false,
      planValue: 'Enterprise Protection ($15K/mo)',
      features: [
        '24/7 security monitoring',
        'Continuous threat detection',
        'Incident response & forensics',
        'Regular compliance audits',
        'Quarterly risk assessments',
        'Policy updates & training',
        'Dedicated security team',
        'Annual certifications'
      ]
    }
  ];

  return (
    <section id="pricing" className="relative py-24 px-4 overflow-hidden">
      <div className="max-w-6xl mx-auto">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <span className="text-xs font-semibold text-teal-300 px-4 py-2 rounded-full border border-teal-500/50 bg-teal-500/10 inline-block mb-4">
            Investment
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Security Packages
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg">
            Flexible security and governance solutions scaled to your organization's needs, 
            from initial assessment to comprehensive enterprise protection.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8">
          {plans.map((plan, index) => (
            <motion.div
              key={index}
              className={`relative group ${plan.featured ? 'md:-mt-4 md:mb-4' : ''}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              {plan.featured && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-10">
                  <span className="px-4 py-1 bg-gradient-to-r from-teal-500 to-emerald-500 rounded-full text-xs font-bold text-slate-900">
                    RECOMMENDED
                  </span>
                </div>
              )}

              <div className={`relative bg-gradient-to-br from-white/[0.07] to-white/[0.02] backdrop-blur-xl border rounded-3xl p-8 h-full flex flex-col transition-all duration-300 ${
                plan.featured 
                  ? 'border-teal-500/50 shadow-lg shadow-teal-500/20' 
                  : 'border-white/10 hover:border-teal-500/30'
              }`}>
                <div className={`absolute top-0 left-0 right-0 h-1 rounded-t-3xl ${
                  plan.featured 
                    ? 'bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500' 
                    : 'bg-gradient-to-r from-blue-500/50 via-teal-500/50 to-emerald-500/50'
                }`} />

                <h3 className="text-xl font-bold text-teal-400 mb-4 text-center">
                  {plan.tier}
                </h3>

                <div className="text-center mb-6">
                  <div className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent mb-2">
                    {plan.price}
                  </div>
                  <div className="text-gray-400 text-sm">
                    {plan.period}
                  </div>
                </div>

                <ul className="space-y-4 mb-8 flex-grow">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-3 text-gray-400">
                      <Check size={18} className="text-emerald-400 mt-0.5 flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => onSelectPlan(plan.planValue)}
                  className={`w-full py-4 rounded-full font-semibold transition-all ${
                    plan.featured
                      ? 'bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 text-white hover:shadow-lg hover:shadow-teal-500/50'
                      : 'border border-white/20 text-white hover:bg-white/10 hover:border-teal-500/50'
                  }`}
                >
                  Get Started
                </motion.button>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============= CASE STUDIES SECTION =============
const CaseStudiesSection = () => {
  const cases = [
    {
      icon: Landmark,
      company: 'Global Financial Services',
      industry: 'Banking & Finance',
      challenge: 'Deploying AI-powered fraud detection while maintaining PCI DSS compliance and protecting customer financial data under GDPR.',
      solution: 'Implemented adversarial-resistant architecture with differential privacy, encrypted model serving, and comprehensive audit trails.',
      results: ['Zero security incidents in 18 months', 'Passed all regulatory audits', 'Detected $12M in fraudulent transactions']
    },
    {
      icon: Stethoscope,
      company: 'Healthcare AI Platform',
      industry: 'Medical Technology',
      challenge: 'Medical imaging AI needed HIPAA compliance, FDA clearance readiness, and protection against model extraction.',
      solution: 'Deployed federated learning architecture with encrypted model updates, comprehensive access controls, and automated compliance monitoring.',
      results: ['HIPAA compliant across 50+ hospitals', 'FDA documentation prepared', 'Zero patient data breaches']
    },
    {
      icon: ShoppingCart,
      company: 'E-Commerce Giant',
      industry: 'Retail Technology',
      challenge: 'Recommendation AI vulnerable to manipulation attacks. Needed GDPR and CCPA compliance for global markets.',
      solution: 'Built robust training pipeline with anomaly detection, implemented user consent management, and deployed explainable recommendations.',
      results: ['Blocked 1,200+ attacks monthly', 'GDPR/CCPA compliant in 12 markets', 'Improved fairness scores by 65%']
    },
    {
      icon: Car,
      company: 'Autonomous Vehicle Company',
      industry: 'Automotive AI',
      challenge: 'Safety-critical perception models needed protection against adversarial attacks on road signs and objects.',
      solution: 'Implemented multi-model ensemble with adversarial training, physical-world attack testing, and comprehensive decision audit trails.',
      results: ['Passed safety certification', '99.7% detection accuracy', 'Complete audit trail for decisions']
    },
    {
      icon: Gavel,
      company: 'Legal Tech Platform',
      industry: 'Legal Services',
      challenge: 'Legal document AI processing attorney-client privileged information. Required absolute confidentiality and explainability.',
      solution: 'Deployed on-premise secure infrastructure with end-to-end encryption, fairness testing, and comprehensive explainability layer.',
      results: ['Zero confidentiality breaches', '95% court acceptance rate', 'Complete explanation for every decision']
    },
    {
      icon: Building2,
      company: 'Enterprise Software',
      industry: 'B2B SaaS',
      challenge: 'AI analytics platform needed SOC 2 Type II certification and EU AI Act compliance for enterprise customers.',
      solution: 'Complete security overhaul including secure API design, role-based access controls, and EU AI Act risk classification framework.',
      results: ['SOC 2 Type II in 6 months', 'EU AI Act compliant', 'Unlocked $20M in contracts']
    }
  ];

  return (
    <section id="case-studies" className="relative py-24 px-4 overflow-hidden bg-slate-950/50">
      <div className="max-w-7xl mx-auto">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <span className="text-xs font-semibold text-teal-300 px-4 py-2 rounded-full border border-teal-500/50 bg-teal-500/10 inline-block mb-4">
            Case Studies
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Protection In Action
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg">
            Real-world examples of how we've secured AI systems, achieved compliance, and 
            prevented costly security incidents across industries.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {cases.map((caseStudy, index) => (
            <motion.div
              key={index}
              className="group"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <div className="bg-gradient-to-br from-white/[0.07] to-white/[0.02] backdrop-blur-xl border border-white/10 rounded-3xl overflow-hidden hover:border-teal-500/30 transition-all duration-300 h-full flex flex-col">
                <div className="bg-gradient-to-r from-blue-500/20 via-teal-500/20 to-emerald-500/20 p-6 flex items-center gap-4">
                  <div className="w-14 h-14 rounded-xl bg-white/10 flex items-center justify-center">
                    <caseStudy.icon className="text-teal-300" size={28} />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-teal-400">
                      {caseStudy.company}
                    </h3>
                    <p className="text-sm text-gray-400">
                      {caseStudy.industry}
                    </p>
                  </div>
                </div>

                <div className="p-6 flex-grow flex flex-col">
                  <div className="mb-4">
                    <p className="text-xs font-bold text-transparent bg-gradient-to-r from-blue-400 to-teal-400 bg-clip-text uppercase tracking-wider mb-2">Challenge:</p>
                    <p className="text-gray-400 text-sm leading-relaxed">
                      {caseStudy.challenge}
                    </p>
                  </div>

                  <div className="mb-4">
                    <p className="text-xs font-bold text-transparent bg-gradient-to-r from-blue-400 to-teal-400 bg-clip-text uppercase tracking-wider mb-2">Solution:</p>
                    <p className="text-gray-400 text-sm leading-relaxed">
                      {caseStudy.solution}
                    </p>
                  </div>

                  <div className="mt-auto bg-emerald-500/10 border-l-4 border-emerald-500 rounded-r-xl p-4">
                    <p className="text-xs font-bold text-emerald-400 uppercase tracking-wider mb-2">Results:</p>
                    <ul className="space-y-1">
                      {caseStudy.results.map((result, i) => (
                        <li key={i} className="text-sm text-gray-300 flex items-start gap-2">
                          <Check size={14} className="text-emerald-400 mt-0.5 flex-shrink-0" />
                          <span>{result}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============= FINAL CTA =============
const FinalCTA = ({ onAssessment }) => {
  return (
    <section className="relative py-24 px-4 overflow-hidden">
      <div className="max-w-4xl mx-auto">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 via-teal-500/20 to-emerald-600/20 blur-3xl" />

        <motion.div
          className="relative bg-gradient-to-br from-white/[0.07] to-white/[0.02] backdrop-blur-xl border border-white/10 rounded-3xl p-12 md:p-16 text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 rounded-t-3xl" />

          <h2 className="text-3xl md:text-4xl font-bold mb-4 bg-gradient-to-r from-teal-400 to-emerald-400 bg-clip-text text-transparent">
            Secure Your AI Systems
          </h2>
          <p className="text-xl text-gray-300 mb-10 max-w-2xl mx-auto">
            Don't wait for a security incident or compliance violation. Protect your AI systems 
            now with comprehensive security and governance from day one.
          </p>

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onAssessment}
            className="px-10 py-4 bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 rounded-full text-white font-bold text-lg hover:shadow-2xl hover:shadow-teal-500/50 transition flex items-center gap-3 mx-auto"
          >
            Request Assessment <Shield size={20} />
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

// ============= FOOTER =============
const Footer = () => {
  return (
    <footer className="relative border-t border-white/10 py-12 px-4 bg-slate-950">
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-4 gap-8 mb-8">
          <div>
            <Link to="/" className="text-white font-bold mb-4 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-gradient-to-r from-blue-500 to-teal-500" />
              AI Factory
            </Link>
            <p className="text-gray-400 text-sm mt-4 leading-relaxed">
              Enterprise AI security, governance, and compliance solutions.
            </p>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-4 text-xs tracking-wider uppercase">Services</h4>
            <ul className="space-y-2 text-gray-400 text-sm">
              <li><Link to="/ai-engineer-augmentation" className="hover:text-teal-400 transition">Staff Augmentation</Link></li>
              <li><Link to="/ai-project-execution" className="hover:text-teal-400 transition">Project Execution</Link></li>
              <li><Link to="/ai-consulting-services" className="hover:text-teal-400 transition">Consulting</Link></li>
              <li><Link to="/ai-security-governance" className="hover:text-teal-400 transition">Security & Governance</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-4 text-xs tracking-wider uppercase">Company</h4>
            <ul className="space-y-2 text-gray-400 text-sm">
              <li><Link to="/#about" className="hover:text-teal-400 transition">About</Link></li>
              <li><Link to="/training" className="hover:text-teal-400 transition">Training Programs</Link></li>
              <li><Link to="/services" className="hover:text-teal-400 transition">All Services</Link></li>
              <li><Link to="/#contact" className="hover:text-teal-400 transition">Contact</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-4 text-xs tracking-wider uppercase">Legal</h4>
            <ul className="space-y-2 text-gray-400 text-sm">
              <li><a href="#" className="hover:text-teal-400 transition">Privacy Policy</a></li>
              <li><a href="#" className="hover:text-teal-400 transition">Terms of Service</a></li>
              <li><a href="#" className="hover:text-teal-400 transition">Cookie Policy</a></li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-400 text-sm">© 2025 AI Factory. All rights reserved.</p>
          <div className="flex gap-6">
            <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-teal-400 transition text-sm">Twitter</a>
            <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-teal-400 transition text-sm">LinkedIn</a>
            <a href="https://discord.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-teal-400 transition text-sm">Discord</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

// ============= MAIN PAGE =============
export default function AISecurityGovernancePage() {
  const [assessmentModal, setAssessmentModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  const handleAssessment = (plan = null) => {
    setSelectedPlan(plan);
    setAssessmentModal(true);
  };

  return (
    <div className="bg-slate-950 min-h-screen overflow-x-hidden text-white">
      <Navbar onAssessment={() => handleAssessment()} />
      <Hero onAssessment={() => handleAssessment()} />
      <ServicesSection />
      <ThreatLandscapeSection />
      <GovernanceSection />
      <ComplianceSection />
      <PricingSection onSelectPlan={handleAssessment} />
      <CaseStudiesSection />
      <FinalCTA onAssessment={() => handleAssessment()} />
      <Footer />

      {/* Security Assessment Modal */}
      <SecurityAssessmentModal
        isOpen={assessmentModal}
        onClose={() => setAssessmentModal(false)}
        selectedPlan={selectedPlan}
      />
    </div>
  );
}